// covered-by: frontends/carrie/src/ask-v2/rail/__tests__/AskV2RightRail.test.tsx
import {
  Buildings,
  CheckCircle,
  Circle,
  CircleNotch,
  FileText,
  Sparkle,
  WarningCircle,
  XCircle,
} from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import { Link as RouterLink } from "@tanstack/react-router";
import { useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { AskV2MemoryScopeIcon } from "./AskV2MemoryScopeIcon";
import { AskV2RailDocumentCard } from "./AskV2RailDocumentCard";
import { AskV2RailEarlierAnswer } from "./AskV2RailEarlierAnswer";
import { AskV2RailMemoryCard } from "./AskV2RailMemoryCard";
import { AskV2RailRelatedClaims } from "./AskV2RailRelatedClaims";
import { AskV2RailReviewCard } from "./AskV2RailReviewCard";
import {
  ASK_V2_RAIL_TOGGLE_TITLE_CLASS,
  AskV2RailSection,
} from "./AskV2RailSection";
import { AskV2RailSubsection } from "./AskV2RailSubsection";
import { hasRailContent } from "./collectRailContent";
import { groupRailCitations } from "./groupRailCitations";
import {
  type AskV2RailConnector,
  type AskV2RailContent,
  type AskV2RailOutputItem,
  type AskV2RailProgressItem,
  type AskV2RailProgressStatus,
  type AskV2RailSectionId,
  type AskV2RailSkill,
} from "./types";

import { CitedClaimsPanel } from "../../components/ask-puntt-cited-claims";
import { resolveReviewListCardThumbnail } from "../../components/ask-puntt-review/resolveReviewListCardThumbnail";
import { clipAtWord } from "../../components/memory-citation/memoryLabels";
import {
  MEMORY_LINK_TO,
  memoryTabSearch,
} from "../../components/memory-citation/memoryLink";
import {
  CONNECTOR_CUSTOM_LOGOS,
  CONNECTOR_PHOSPHOR_ICONS,
  CONNECTOR_PHOSPHOR_WEIGHT,
} from "../../utils/askPunttConnectorLogos";
import { isDemoEnterprise } from "../../utils/auth";
import {
  EDGE_LABEL_CHARS,
  type MemoryCitation,
} from "../../utils/memoryCitationFields";
import { ASK_V2_CHAT_PATH, buildAskV2ChatReviewPath } from "../paths";
import { useAskPunttReview } from "../review/useAskPunttReview";

/**
 * The words a plain memory row shows: the label's length, cut at a whole word.
 * An edge that carries the whole memory (`memoryText`) would otherwise print all
 * of it in one link; the row stays the one line or two it always was, and a
 * label warhol already cut (`clipped`) is shown as it is.
 */
function plainRowText(memory: MemoryCitation): string {
  return memory.clipped
    ? memory.text
    : clipAtWord(memory.text, EDGE_LABEL_CHARS);
}

type AskV2RightRailProps = {
  content: AskV2RailContent;
  sectionOpen: Record<AskV2RailSectionId, boolean>;
  onSectionOpenChange: (id: AskV2RailSectionId, open: boolean) => void;
  onReviewOpen?: (reviewId: string) => void;
  /** When false the panel stays mounted and collapses, matching the nav width transition. */
  open?: boolean;
  /** Back to the latest answer's citations after scrolling to an earlier one. */
  onShowLatestCitations?: () => void;
  /** Sections only — parent supplies the rail surface and width. */
  embedded?: boolean;
};

// why: primary nav collapses with the same 200ms ease-out width transition
export const ASK_V2_RAIL_WIDTH_CLASS =
  "h-full shrink-0 overflow-hidden transition-[width] duration-200 ease-out";

/**
 * The wire tokens the reviews API answers with, mapped onto the chip copy the
 * catalog already ships for them.
 *
 * `listReviews` (christo, `askPunttProjectFeed.ts`) returns
 * `in_review | completed | idle` — lowercase snake, a value for code to branch
 * on — and the chip printed it verbatim, so the Progress section read
 * "in_review" to every user in every language. The legacy Reviews panel never
 * showed that: `ProjectReviewsPanel` runs the same field through `toTitleCase`
 * before rendering it.
 *
 * A lookup and not a blanket title-case, because the OTHER source of this
 * field is `chat.reviewStatus`, which is the agent's own sentence ("Reviewing
 * artwork"). That is already copy and passes through untouched.
 *
 * Reusing the existing `ui` keys rather than adding rail-local ones: these are
 * the exact two chips this status has always meant, already translated in all
 * six catalogs, and one string cannot drift from itself.
 */
const PROGRESS_STATUS_LABEL_KEY: Record<string, string> = {
  in_review: "inReview",
  completed: "completed",
};

function ProgressIcon({
  status,
  size = 16,
}: {
  status: AskV2RailProgressStatus;
  size?: number;
}) {
  if (status === "done") {
    return (
      <CheckCircle
        size={size}
        weight="fill"
        className="shrink-0 text-puntt-neutral-grayA-11"
        aria-hidden="true"
      />
    );
  }
  if (status === "failed") {
    // A check that ran and did not do the work. It must not draw the tick the
    // three older states forced on it: "timed out" is not "complete".
    return (
      <XCircle
        size={size}
        weight="fill"
        className="text-tomato-900 shrink-0"
        aria-hidden="true"
      />
    );
  }
  if (status === "blocked") {
    // A check that could not finish: not a tick (it did not run) and not a
    // spinner (nothing is still happening). Without this row a skipped check
    // is indistinguishable from one that has not started yet.
    //
    // Groundwork, not a wired path: nothing emits `status: "blocked"` on a rail
    // item yet. The live rows are the turn-progress channel in
    // `P0-warhol-check-progress-midturn`; until that lands this branch is
    // reached only by a test that builds the item by hand (see the task's
    // spec 0033, "The rail carries a blocked row").
    return (
      <WarningCircle
        size={16}
        weight="fill"
        className="shrink-0 text-puntt-neutral-gray-10"
        aria-hidden="true"
      />
    );
  }
  if (status === "active") {
    return (
      <CircleNotch
        size={16}
        className="shrink-0 animate-spin text-puntt-neutral-gray-9"
        aria-hidden="true"
      />
    );
  }
  return (
    <Circle
      size={size}
      className="shrink-0 text-puntt-neutral-gray-8"
      aria-hidden="true"
    />
  );
}

// why: the k/n count under a step is noise; a skipped or failure detail on the same field stays
function shownProgressDetail(detail: string | undefined): string | undefined {
  if (detail == null || detail === "") return undefined;
  if (/^\d+\s*\/\s*\d+/.test(detail.trim())) return undefined;
  return detail;
}

/**
 * One rail progress row.
 *
 * `sentence` is passed in rather than rebuilt here: it carries the catalog
 * lookup and the `detail` join, and one definition of a row's spoken name is
 * what keeps the rail's announcement in step with what it draws.
 *
 * There is no nesting. The per-check rows #5685 put one level down were removed
 * -- the rail shows the plan's own steps, and the checks behind a step are
 * per-tool-call detail for the thread.
 */
function ProgressRow({
  item,
  sentence,
}: {
  item: AskV2RailProgressItem;
  sentence: (item: AskV2RailProgressItem) => string;
}) {
  const detail = shownProgressDetail(item.detail);
  return (
    <li
      className="flex items-start gap-2 text-[13px] text-puntt-neutral-gray-12"
      data-testid={`ask-v2-rail-progress-item-${item.id}`}
      aria-label={sentence(item)}
    >
      <ProgressIcon status={item.status} size={16} />
      <span className="min-w-0">
        <span
          className={`block leading-snug ${item.status === "done" ? "text-puntt-neutral-gray-10 line-through" : ""}`}
        >
          {item.label}
        </span>
        {detail ? (
          <span className="block text-[12px] text-puntt-neutral-gray-10">
            {detail}
          </span>
        ) : null}
      </span>
    </li>
  );
}

function RailConnectorChip({ connector }: { connector: AskV2RailConnector }) {
  const PhosphorIcon = CONNECTOR_PHOSPHOR_ICONS[connector.id];
  const customLogoSrc = CONNECTOR_CUSTOM_LOGOS[connector.id];

  return (
    <span
      className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-puntt-neutral-gray-3 px-2.5 py-1 text-[12px] font-medium text-puntt-neutral-gray-12"
      data-testid={`ask-v2-rail-connector-${connector.id}`}
      aria-label={connector.name}
    >
      <span
        aria-hidden="true"
        className="inline-flex size-3.5 shrink-0 items-center justify-center"
      >
        {PhosphorIcon ? (
          <PhosphorIcon size={14} weight={CONNECTOR_PHOSPHOR_WEIGHT} />
        ) : customLogoSrc ? (
          <img
            src={customLogoSrc}
            alt=""
            width={14}
            height={14}
            className="size-3.5 object-contain"
            draggable={false}
          />
        ) : (
          <Buildings size={14} aria-hidden="true" />
        )}
      </span>
      <span className="min-w-0 truncate">{connector.name}</span>
    </span>
  );
}

function RailSkillChip({ skill }: { skill: AskV2RailSkill }) {
  return (
    <span
      className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-puntt-neutral-gray-3 px-2.5 py-1 text-[12px] font-medium text-puntt-neutral-gray-12"
      data-testid={`ask-v2-rail-skill-${skill.id}`}
      aria-label={skill.name}
    >
      <Sparkle
        size={14}
        className="shrink-0 text-puntt-neutral-gray-10"
        aria-hidden="true"
      />
      <span className="min-w-0 truncate">{skill.name}</span>
    </span>
  );
}

function RailOutputReviewCard({
  item,
  onReviewOpen,
}: {
  item: AskV2RailOutputItem & { kind: "review" };
  onReviewOpen: (reviewId: string) => void;
}) {
  const { data } = useAskPunttReview(item.id);
  const live = data?.review;
  const thumbnailUrl = resolveReviewListCardThumbnail({
    review: live,
    payloadThumbnailUrl: item.thumbnailUrl,
    isDemoEnterprise: isDemoEnterprise(),
  });

  return (
    <AskV2RailReviewCard
      testIdPrefix="ask-v2-rail-output-review"
      data-testid={`ask-v2-rail-output-review-${item.id}`}
      onClick={() => onReviewOpen(item.id)}
      item={{
        id: item.id,
        name: live?.title?.trim() || item.title,
        versionNumber: item.versionNumber,
        thumbnailUrl,
        aiCommentCount: live?.commentsSummary?.ai ?? item.aiCommentCount,
        humanCommentCount:
          live?.commentsSummary?.human ?? item.humanCommentCount,
        isGenerating: item.isGenerating,
      }}
    />
  );
}

/**
 * Past-review rows carry a word for what they are. Chats and memories do
 * not: those sections already name the kind, and a chip on the row repeated it.
 */
/** One hue per kind, matching the inline link of the same evidence. */
const EVIDENCE_TAG_CLASS: Record<"Review", string> = {
  Review: "puntt-link",
};

/**
 * The word the tag SHOWS, per catalog.
 *
 * `kind` stays the English discriminant the testid and the colour are keyed on
 * -- those are contracts with locators and CSS, not copy -- while the rendered
 * word comes from the catalog like every other label in this rail. Rendering
 * `kind` directly put "Review" in front of Spanish, French, German,
 * Portuguese and Arabic readers.
 */
const EVIDENCE_TAG_LABEL_KEY: Record<"Review", string> = {
  Review: "askV2.rail.evidenceTagReview",
};

/**
 * `rowId` is the row's own id, not its index: these tags render once per
 * citation, so a rail with two findings would otherwise put the same testid
 * on two nodes and every locator for it resolves to 2 elements.
 */
function EvidenceTag({ kind, rowId }: { kind: "Review"; rowId: string }) {
  const { t } = useTranslation("ui");

  return (
    <span
      data-testid={`ask-v2-rail-evidence-tag-${kind.toLowerCase()}-${rowId}`}
      className={`shrink-0 rounded-full bg-puntt-neutral-gray-3 px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide ${EVIDENCE_TAG_CLASS[kind]}`}
    >
      {t(EVIDENCE_TAG_LABEL_KEY[kind])}
    </span>
  );
}

export function AskV2RightRail({
  content,
  sectionOpen,
  onSectionOpenChange,
  onReviewOpen,
  open = true,
  onShowLatestCitations,
  embedded = false,
}: AskV2RightRailProps) {
  const { t } = useTranslation("ui");
  const railRef = useRef<HTMLElement>(null);
  // why: the rail is unmounted until content arrives, so a collapsed rail that
  // fills in mid-stream mounts without re-running an [open]-only effect
  const railMounted = hasRailContent(content);

  // why: aria-hidden alone leaves the collapsed buttons in the tab order
  useLayoutEffect(() => {
    const node = railRef.current;
    if (!node) return;
    if (open) node.removeAttribute("inert");
    else node.setAttribute("inert", "");
  }, [open, railMounted]);

  if (!railMounted) return null;

  const rawProgressStatus = content.progress?.status ?? "";
  const progressStatusKey =
    PROGRESS_STATUS_LABEL_KEY[rawProgressStatus.trim().toLowerCase()];
  const progressStatusLabel = progressStatusKey
    ? t(progressStatusKey)
    : rawProgressStatus;

  const progressSteps = content.progress?.items ?? [];
  /** A row's whole sentence: its name, its state, and the reason it gave. */
  const progressStepSentence = (item: AskV2RailProgressItem) => {
    const status = t(`askV2.rail.progressStepStatus.${item.status}`);
    const detail = shownProgressDetail(item.detail);
    return detail
      ? t("askV2.rail.progressStepAriaDetail", {
          label: item.label,
          status,
          detail,
        })
      : t("askV2.rail.progressStepAria", {
          label: item.label,
          status,
        });
  };
  /**
   * The step a reader is waiting on: what is RUNNING first, then what is stuck,
   * and once neither exists, the last one that finished — so the end of the run
   * is spoken too instead of the region falling silent on it.
   *
   * `active` outranks `blocked` because `blocked` is terminal and `active` is
   * not. Ranked together in one `find`, the FIRST of either won, so a check
   * blocked early in the spine pinned this region to itself for the rest of the
   * run: plan -> load -> agents(blocked) -> inspect -> comments -> complete
   * announced "agents, blocked: ..." once and then nothing, because the row it
   * was pinned to never changes again. That is the same silent review the region
   * was added to end, re-entered through the one row that cannot move. A stuck
   * check is still announced the moment nothing is running, which is when it is
   * the thing a reader is waiting on.
   *
   * `failed` sits with `blocked`, not outside the ranking. Both are rows that
   * stopped and will not move again, and both are the reason a reader is still
   * here. Left out, a run ending `load(done) -> inspect(failed)` skipped the
   * failed row and reverse-found the earlier `done` one, so the last thing a
   * screen reader heard about a review that did NOT do the work was
   * "Assets loaded, complete"; a plan whose only rows failed announced nothing
   * at all. `ProgressIcon` has drawn `failed` its own tomato X since the state
   * existed — this is the same row, spoken.
   */
  const liveProgressStep =
    progressSteps.find((item) => item.status === "active") ??
    progressSteps.find(
      (item) => item.status === "blocked" || item.status === "failed",
    ) ??
    [...progressSteps].reverse().find((item) => item.status === "done");
  const progressAnnouncement = liveProgressStep
    ? progressStepSentence(liveProgressStep)
    : "";

  const sections = (
    <>
      {content.progress ? (
        <AskV2RailSection
          id="progress"
          title={t("askV2.rail.progress")}
          open={sectionOpen.progress}
          onOpenChange={(open) => onSectionOpenChange("progress", open)}
        >
          {content.progress.status &&
          // why: [].every(done) is true — an empty shell still needs its status
          !(
            content.progress.items.length > 0 &&
            content.progress.items.every((item) => item.status === "done")
          ) ? (
            <p
              className="mb-3 text-[11px] font-medium text-puntt-neutral-gray-9"
              data-testid="ask-v2-rail-progress-status"
            >
              {progressStatusLabel}
            </p>
          ) : null}
          {/*
              The live region is the summary, not the row list: a listitem's
              `aria-label` REPLACES its contents, and a row advances by swapping
              an icon and rewriting an attribute, so nothing inside the list is
              ever announced. `ReviewProgressBar` splits it the same way.
            */}
          <p
            className="sr-only"
            role="status"
            data-testid="ask-v2-rail-progress-announcement"
          >
            {progressAnnouncement}
          </p>
          <ul
            className="flex flex-col gap-2"
            data-testid="ask-v2-rail-progress-list"
          >
            {content.progress.items.map((item) => (
              <ProgressRow
                key={item.id}
                item={item}
                sentence={progressStepSentence}
              />
            ))}
          </ul>
          {/* why: verified findings are pending comments and render in the thread, not under these steps */}
        </AskV2RailSection>
      ) : null}

      {content.output ? (
        <AskV2RailSection
          id="output"
          title={t("output")}
          open={sectionOpen.output}
          onOpenChange={(open) => onSectionOpenChange("output", open)}
        >
          <ul
            className="flex min-w-0 flex-col gap-2"
            data-testid="ask-v2-rail-output-list"
          >
            {content.output.items.map((item) => {
              const reviewCard =
                item.kind === "review" && onReviewOpen ? (
                  <RailOutputReviewCard
                    item={item as AskV2RailOutputItem & { kind: "review" }}
                    onReviewOpen={onReviewOpen}
                  />
                ) : null;

              return (
                <li
                  key={item.id}
                  className="min-w-0"
                  data-testid={`ask-v2-rail-output-item-${item.id}`}
                >
                  {reviewCard ?? (
                    <div className="flex items-center gap-3 rounded-lg border border-puntt-neutral-gray-5 bg-base-white px-3 py-2">
                      {item.thumbnailUrl ? (
                        <img
                          src={item.thumbnailUrl}
                          alt=""
                          className="size-10 shrink-0 rounded-md object-cover"
                        />
                      ) : (
                        <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-puntt-neutral-gray-3 text-puntt-neutral-gray-10">
                          <FileText size={16} aria-hidden="true" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[13px] font-medium text-puntt-neutral-gray-12">
                          {item.title}
                        </p>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </AskV2RailSection>
      ) : null}

      {content.context ? (
        <AskV2RailSection
          id="context"
          title={t("context")}
          open={sectionOpen.context}
          onOpenChange={(open) => onSectionOpenChange("context", open)}
        >
          <div
            className="flex flex-col gap-4"
            data-testid="ask-v2-rail-context-body"
          >
            {content.context.overview ? (
              <div data-testid="ask-v2-rail-overview">
                <Text
                  as="span"
                  size="1"
                  weight="medium"
                  className={`mb-1 block ${ASK_V2_RAIL_TOGGLE_TITLE_CLASS}`}
                >
                  {t("askV2.rail.overview")}
                </Text>
                <p className="text-[13px] leading-relaxed text-puntt-neutral-gray-11">
                  {content.context.overview}
                </p>
              </div>
            ) : null}
            {content.context.upcomingWork ? (
              <div data-testid="ask-v2-rail-upcoming">
                <Text
                  as="span"
                  size="1"
                  weight="medium"
                  className={`mb-1 block ${ASK_V2_RAIL_TOGGLE_TITLE_CLASS}`}
                >
                  {t("campaignDocsPanel.nextSteps")}
                </Text>
                <p className="text-[13px] leading-relaxed text-puntt-neutral-gray-11">
                  {content.context.upcomingWork}
                </p>
              </div>
            ) : null}
            {(() => {
              // One list held rules, conversations and memories at once, and
              // only the rules were linkable. Split by what the evidence IS,
              // so each row can go somewhere a reader can verify it.
              const cited = groupRailCitations(content.context.citedClaims);
              // why-not: no empty-memory row — an unnamed memory has no label to show and no term to search the Memories tab for.
              const citedMemories = cited.memories.filter(
                (memory) => memory.text.trim() !== "",
              );
              // A memory that names the sentence it supports is a numbered card; one that does not (an older chat) stays the plain row it was.
              // why numbered by the answer's map and not by position here: the M on the chip in the thread comes from the same map.
              const memoryNumbers = content.context?.memoryNumbers;
              const supportedMemories = citedMemories
                .filter(
                  (memory): memory is typeof memory & { supportText: string } =>
                    !!memory.supportText,
                )
                .map((memory, position) => ({
                  memory,
                  number: memoryNumbers?.get(memory.id) ?? position + 1,
                }))
                .sort((a, b) => a.number - b.number);
              const plainMemories = citedMemories.filter(
                (memory) => !memory.supportText,
              );
              return (
                <>
                  {content.context.citationsFromEarlierAnswer ? (
                    <AskV2RailEarlierAnswer
                      onShowLatest={onShowLatestCitations}
                    />
                  ) : null}
                  {cited.claims.length > 0 ? (
                    <AskV2RailSubsection
                      id="cited-claims"
                      title={t("citedClaimsPanel.title")}
                    >
                      <div data-testid="ask-v2-rail-cited-claims">
                        <CitedClaimsPanel
                          citations={cited.claims}
                          variant="section"
                          testIdPrefix="ask-v2-rail-cited-claims"
                          hideTitle
                          numbers={content.context.citationNumbers}
                        />
                      </div>
                    </AskV2RailSubsection>
                  ) : null}
                  <AskV2RailRelatedClaims
                    related={cited.related}
                    // why the thread's count: cited cards carry thread
                    // numbers, so related ones start past all of them.
                    citedCount={Math.max(
                      cited.claims.length,
                      content.context.citationNumbers?.size ?? 0,
                    )}
                  />
                  {cited.chats.length > 0 ? (
                    <AskV2RailSubsection
                      id="cited-chats"
                      title={t("askV2.rail.citedChats")}
                    >
                      <ul
                        className="flex flex-col gap-2"
                        data-testid="ask-v2-rail-cited-chats"
                      >
                        {cited.chats.map((chat) => {
                          // One title for BOTH the visible row and its
                          // accessible name. The two were derived separately
                          // and fell back differently -- the text to the
                          // catalog's untitled label, the name to `chat.id` --
                          // so a conversation the graph never named showed
                          // "Untitled conversation" and announced a
                          // 24-character Mongo id read out character by
                          // character. That also left the visible label absent
                          // from the accessible name (WCAG 2.5.3 Label in
                          // Name), which is the string voice control matches
                          // on, so the row could be seen and not spoken to.
                          //
                          // Reusing `citedChatUntitled` rather than adding a
                          // second aria key: the fallback is the row's words,
                          // and one catalog string cannot drift from itself.
                          const title =
                            chat.title || t("askV2.rail.citedChatUntitled");
                          return (
                            <li key={chat.id}>
                              <RouterLink
                                to={ASK_V2_CHAT_PATH}
                                params={{ chatId: chat.id }}
                                data-testid={`ask-v2-rail-cited-chat-${chat.id}`}
                                aria-label={t("askV2.rail.citedChatAria", {
                                  title,
                                })}
                                className="puntt-link-chat text-[13px] leading-snug underline decoration-dotted underline-offset-2"
                              >
                                {title}
                              </RouterLink>
                            </li>
                          );
                        })}
                      </ul>
                    </AskV2RailSubsection>
                  ) : null}
                  {citedMemories.length > 0 ? (
                    <AskV2RailSubsection
                      id="cited-memories"
                      title={t("askV2.rail.citedMemories")}
                    >
                      {supportedMemories.length > 0 ? (
                        <>
                          <p
                            data-testid="ask-v2-rail-memory-cards-subtitle"
                            className="mb-2 text-[12px] leading-4 text-puntt-neutral-gray-11"
                          >
                            {t("askV2.rail.memoryCard.subtitle")}
                          </p>
                          <ul
                            // why a grid here: the cards are its subgrids, so one chip column fits the widest chip of the whole list (`AskV2RailMemoryCard`).
                            className="grid grid-cols-[auto_minmax(0,1fr)_auto] gap-2"
                            data-testid="ask-v2-rail-memory-cards"
                          >
                            {supportedMemories.map(({ memory, number }) => (
                              <AskV2RailMemoryCard
                                key={memory.id}
                                memory={memory}
                                number={number}
                                scope={content.context?.citedMessageId ?? ""}
                              />
                            ))}
                          </ul>
                        </>
                      ) : null}
                      {plainMemories.length > 0 ? (
                        <ul
                          className={`flex flex-col gap-2 ${
                            supportedMemories.length > 0 ? "mt-3" : ""
                          }`}
                          data-testid="ask-v2-rail-cited-memories"
                        >
                          {plainMemories.map((memory) => (
                            <li key={memory.id}>
                              <RouterLink
                                to={MEMORY_LINK_TO}
                                search={memoryTabSearch(memory.text)}
                                data-testid={`ask-v2-rail-cited-memory-${memory.id}`}
                                aria-label={t("askV2.rail.citedMemoryAria", {
                                  text: plainRowText(memory),
                                })}
                                className="puntt-link-memory text-[13px] leading-snug underline decoration-dotted underline-offset-2"
                              >
                                {plainRowText(memory)}
                              </RouterLink>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </AskV2RailSubsection>
                  ) : null}
                  {cited.findings.length > 0 ? (
                    <AskV2RailSubsection
                      id="cited-findings"
                      title={t("askV2.rail.citedFindings")}
                    >
                      <ul
                        className="flex flex-col gap-2"
                        data-testid="ask-v2-rail-cited-findings"
                      >
                        {cited.findings.map((finding) => {
                          const link = finding.review
                            ? buildAskV2ChatReviewPath(
                                finding.review.chatId,
                                finding.review.reviewId,
                                finding.review.commentId,
                              )
                            : null;
                          return (
                            <li
                              key={finding.id}
                              className="flex items-start gap-1.5"
                            >
                              <EvidenceTag kind="Review" rowId={finding.id} />
                              {link ? (
                                <RouterLink
                                  // why `as never`: the Ask v2 routes are not in
                                  // the typed route tree; useAskV2ReviewWorkspace
                                  // navigates to this path the same way.
                                  to={link.to as never}
                                  params={link.params as never}
                                  data-testid={`ask-v2-rail-cited-finding-${finding.id}`}
                                  aria-label={t("askV2.rail.citedFindingAria", {
                                    text: finding.text,
                                  })}
                                  className="puntt-link-chat text-[13px] leading-snug underline decoration-dotted underline-offset-2"
                                >
                                  {finding.text}
                                </RouterLink>
                              ) : (
                                // why not a link: a v1 finding names a ticket,
                                // and the Ask v2 tree does not register
                                // /tickets (routes.ts), so a ticket link from
                                // here lands nowhere. It is named instead.
                                <Text
                                  as="span"
                                  data-testid={`ask-v2-rail-cited-finding-${finding.id}`}
                                  className="text-[13px] leading-snug"
                                >
                                  {finding.text}
                                </Text>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </AskV2RailSubsection>
                  ) : null}
                </>
              );
            })()}
            {content.context.memories.length > 0 ? (
              <AskV2RailSubsection
                id="memories"
                title={t("askV2.rail.memories")}
              >
                <ul
                  className="flex flex-col gap-3"
                  data-testid="ask-v2-rail-memories"
                >
                  {content.context.memories.map((memory) => (
                    <li
                      key={memory.scope}
                      className="flex items-start gap-2 text-[13px] leading-snug text-puntt-neutral-gray-12"
                      data-testid={`ask-v2-rail-memory-${memory.scope}`}
                    >
                      <AskV2MemoryScopeIcon scope={memory.scope} />
                      <span className="line-clamp-3 min-w-0">
                        {memory.text}
                      </span>
                    </li>
                  ))}
                </ul>
              </AskV2RailSubsection>
            ) : null}
            {content.context.documents.length > 0 ? (
              <AskV2RailSubsection
                id="documents"
                title={t("campaignDocsPanel.referenceDocuments")}
              >
                <ul
                  className="flex flex-col gap-2"
                  data-testid="ask-v2-rail-documents"
                >
                  {content.context.documents.map((document) => (
                    <li key={document.id}>
                      <AskV2RailDocumentCard document={document} />
                    </li>
                  ))}
                </ul>
              </AskV2RailSubsection>
            ) : null}
            {content.context.connectors.length > 0 ? (
              <AskV2RailSubsection id="connectors" title={t("connectors")}>
                <div
                  className="flex flex-wrap gap-2"
                  data-testid="ask-v2-rail-connectors"
                >
                  {content.context.connectors.map((connector) => (
                    <RailConnectorChip
                      key={connector.id}
                      connector={connector}
                    />
                  ))}
                </div>
              </AskV2RailSubsection>
            ) : null}
            {content.context.skills.length > 0 ? (
              <AskV2RailSubsection id="skills" title={t("askV2.rail.skills")}>
                <div
                  className="flex flex-wrap gap-2"
                  data-testid="ask-v2-rail-skills"
                >
                  {content.context.skills.map((skill) => (
                    <RailSkillChip key={skill.id} skill={skill} />
                  ))}
                </div>
              </AskV2RailSubsection>
            ) : null}
          </div>
        </AskV2RailSection>
      ) : null}
    </>
  );

  if (embedded) {
    return (
      <div
        className={open ? "flex flex-col gap-3" : "hidden"}
        data-testid="ask-v2-right-rail"
        data-open={open ? "true" : "false"}
        aria-hidden={open ? undefined : true}
      >
        {sections}
      </div>
    );
  }

  return (
    <aside
      ref={railRef}
      className={`${ASK_V2_RAIL_WIDTH_CLASS} ${open ? "w-[320px]" : "w-0"}`}
      data-testid="ask-v2-right-rail"
      data-open={open ? "true" : "false"}
      aria-hidden={open ? undefined : true}
    >
      <div className="ask-puntt-surface flex h-full w-[320px] flex-col gap-3 overflow-y-auto overscroll-contain p-3 [scrollbar-width:thin]">
        {sections}
      </div>
    </aside>
  );
}
